import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { RowActionButtons } from '@/components/tables/RowActionButtons';
import { Modal } from '@/components/dialogs/Modal';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { TextField } from '@/components/forms/TextField';
import { Select2Field } from '@/components/forms/Select2Field';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import {
  CONNECTOR_TYPES,
  connectionsApi,
  connectorFieldVisibility,
  connectorTypeValueFromLabel,
  dataSourcesApi,
  isTerminalConnectorType,
  type ConnectionRow,
  type DataSourceRow,
} from '@/api/services/connectors';

const PAGE = { skipCount: 0, maxResultCount: 1000 };

function EnabledSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="form-check form-switch d-inline-block m-0">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connection modal
// ---------------------------------------------------------------------------

interface ConnectionForm {
  name: string;
  dataSourceType: string;
  url: string;
  login: string;
  password: string;
  port: string;
  database: string;
  schema: string;
  managerLogin: string;
}

type ConnModalState = { mode: 'create' } | { mode: 'edit'; row: ConnectionRow } | null;

function ConnectionModal({
  tenantId,
  state,
  onClose,
}: {
  tenantId: string;
  state: ConnModalState;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { register, handleSubmit, reset, watch, control } = useForm<ConnectionForm>();

  const editId = state?.mode === 'edit' ? state.row.id : null;

  // On edit, load the connection's detail fields (the list row only has name/type/enabled).
  const detailQuery = useQuery({
    queryKey: ['connection-detail', tenantId, editId],
    queryFn: () => connectionsApi.get(editId as string, tenantId),
    enabled: !!editId,
  });

  useMemo(() => {
    if (state?.mode === 'create') {
      reset({
        name: '',
        dataSourceType: '0',
        url: '',
        login: '',
        password: '',
        port: '',
        database: '',
        schema: '',
        managerLogin: '',
      });
    } else if (state?.mode === 'edit' && detailQuery.data) {
      const d = detailQuery.data;
      const typeValue =
        connectorTypeValueFromLabel(state.row.dataSourceType) ?? d.dataSourceType ?? 0;
      reset({
        name: d.name ?? '',
        dataSourceType: String(typeValue),
        url: d.url ?? '',
        login: d.login ?? '',
        password: d.password ?? '',
        port: d.port ?? '',
        database: d.database ?? '',
        schema: d.schema ?? '',
        managerLogin: d.managerLogin ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, detailQuery.data]);

  const typeValue = Number(watch('dataSourceType') ?? '0');
  const vis = connectorFieldVisibility(Number.isNaN(typeValue) ? null : typeValue);

  const save = useMutation({
    mutationFn: (values: ConnectionForm) => {
      const payload = {
        tenantId,
        name: values.name.trim(),
        dataSourceType: Number(values.dataSourceType),
        url: values.url || null,
        login: values.login || null,
        password: values.password || null,
        port: values.port || null,
        database: vis.database ? values.database || null : null,
        schema: vis.schema ? values.schema || null : null,
        managerLogin: vis.manager ? values.managerLogin || null : null,
      };
      if (state?.mode === 'edit') {
        return connectionsApi.update({ ...payload, id: state.row.id, enabled: state.row.enabled });
      }
      return connectionsApi.create(payload);
    },
    onSuccess: () => {
      toast.success('Connection saved.');
      void qc.invalidateQueries({ queryKey: ['connections', tenantId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save connection.'),
  });

  if (!state) return null;

  const loadingDetail = state.mode === 'edit' && detailQuery.isLoading;

  return (
    <Modal
      show
      title={state.mode === 'create' ? 'Add Connection' : 'Edit Connection'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-dark" onClick={onClose} disabled={save.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            form="connection-form"
            className="btn btn-primary"
            disabled={save.isPending || loadingDetail}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {loadingDetail ? (
        <Spinner />
      ) : (
        <form id="connection-form" onSubmit={handleSubmit((v) => save.mutate(v))} noValidate>
          <div className="row g-3">
            <div className="col-md-6">
              <TextField label="Name" {...register('name')} />
            </div>
            <div className="col-md-6">
              <Select2Field
                control={control}
                name="dataSourceType"
                label="Connector Type"
                allowClear={false}
                options={CONNECTOR_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              />
            </div>
            {vis.port && (
              <div className="col-md-6">
                <TextField label="Port" {...register('port')} />
              </div>
            )}
            {vis.url && (
              <div className="col-md-6">
                <TextField label="Url" {...register('url')} />
              </div>
            )}
            {vis.login && (
              <div className="col-md-6">
                <TextField label="Login" {...register('login')} />
              </div>
            )}
            {vis.password && (
              <div className="col-md-6">
                <TextField label="Password" type="password" {...register('password')} />
              </div>
            )}
            {vis.database && (
              <div className="col-md-6">
                <TextField label="Database" {...register('database')} />
              </div>
            )}
            {vis.schema && (
              <div className="col-md-6">
                <TextField label="Schema" {...register('schema')} />
              </div>
            )}
            {vis.manager && (
              <div className="col-md-6">
                <TextField label="Manager Login" {...register('managerLogin')} />
              </div>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Data source modal
// ---------------------------------------------------------------------------

interface DataSourceForm {
  name: string;
  server: string;
  accountLogin: string;
  password: string;
}

type DsModalState = { mode: 'create' } | { mode: 'edit'; row: DataSourceRow } | null;

function DataSourceModal({
  tenantId,
  connectionId,
  state,
  onClose,
}: {
  tenantId: string;
  connectionId: string;
  state: DsModalState;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { register, handleSubmit, reset } = useForm<DataSourceForm>();

  const editId = state?.mode === 'edit' ? state.row.id : null;

  // The list grid masks Server/AccountLogin/Password, so load the full record
  // on edit (legacy EditModal.OnGetAsync does the same via GetAsync).
  const detailQuery = useQuery({
    queryKey: ['data-source-detail', tenantId, editId],
    queryFn: () => dataSourcesApi.get(editId as number, tenantId),
    enabled: editId !== null,
  });

  useMemo(() => {
    if (state?.mode === 'create') {
      reset({ name: '', server: '', accountLogin: '', password: '' });
    } else if (state?.mode === 'edit' && detailQuery.data) {
      const d = detailQuery.data;
      reset({
        name: d.name ?? '',
        server: d.server ?? '',
        accountLogin: d.accountLogin ?? '',
        password: d.password ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, detailQuery.data]);

  const save = useMutation({
    mutationFn: (values: DataSourceForm) => {
      const common = {
        tenantId,
        name: values.name.trim(),
        server: values.server || null,
        accountLogin: values.accountLogin || null,
        password: values.password || null,
      };
      if (state?.mode === 'edit') {
        return dataSourcesApi.update({ ...common, id: state.row.id, dataSourceConnectionId: connectionId });
      }
      return dataSourcesApi.create({ ...common, dataSourceConectionId: connectionId });
    },
    onSuccess: () => {
      toast.success('Data source saved.');
      void qc.invalidateQueries({ queryKey: ['data-sources', tenantId, connectionId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save data source.'),
  });

  if (!state) return null;

  const loadingDetail = state.mode === 'edit' && detailQuery.isLoading;

  return (
    <Modal
      show
      title={state.mode === 'create' ? 'Add Data Source' : 'Edit Data Source'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-dark" onClick={onClose} disabled={save.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            form="data-source-form"
            className="btn btn-primary"
            disabled={save.isPending || loadingDetail}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {loadingDetail ? (
        <Spinner />
      ) : (
        <form id="data-source-form" onSubmit={handleSubmit((v) => save.mutate(v))} noValidate>
          <TextField label="Name" {...register('name')} />
          <TextField label="Server" {...register('server')} />
          <TextField label="Account Login" {...register('accountLogin')} />
          <TextField label="Password" type="password" {...register('password')} />
        </form>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Data sources view (drill-down)
// ---------------------------------------------------------------------------

function DataSourcesView({
  tenantId,
  connection,
  onBack,
}: {
  tenantId: string;
  connection: ConnectionRow;
  onBack: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const listQuery = useQuery({
    queryKey: ['data-sources', tenantId, connection.id],
    queryFn: () => dataSourcesApi.getList(tenantId, connection.id, PAGE),
  });

  const [modal, setModal] = useState<DsModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<DataSourceRow | null>(null);

  const setEnabled = useMutation({
    mutationFn: (p: { id: number; enabled: boolean }) =>
      dataSourcesApi.setEnabled(p.id, p.enabled, tenantId),
    onSuccess: (_data, p) => {
      toast.success(p.enabled ? 'Connector enabled.' : 'Connector disabled.');
      // Parent "Enabled" can be computed from children, so refresh both (legacy
      // connector.js reloads dataSourceManager AND connectorManager).
      void qc.invalidateQueries({ queryKey: ['data-sources', tenantId, connection.id] });
      void qc.invalidateQueries({ queryKey: ['connections', tenantId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => dataSourcesApi.remove(id, tenantId),
    onSuccess: () => {
      toast.success('Data source deleted.');
      setDeleteTarget(null);
      void qc.invalidateQueries({ queryKey: ['data-sources', tenantId, connection.id] });
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete.');
      setDeleteTarget(null);
    },
  });

  const rows = listQuery.data?.items ?? [];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-dark btn-sm" onClick={onBack}>
            <i className="fa fa-arrow-left me-1" />
            Back
          </button>
          <h5>
            Data Sources for <span className="text-primary">{connection.name}</span>
          </h5>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>
          <i className="fa fa-plus me-1" />
          Add Data Source
        </button>
      </div>

      {listQuery.isLoading ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => listQuery.refetch()} />
      ) : (
        <table className="table dataTable table-striped table-hover w-100 mb-0">
          <thead>
            <tr>
              <th className="text-start">Name</th>
              <th className="text-start">Server</th>
              <th className="text-start">Account Login</th>
              <th className="text-center">Enabled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState message="No data sources." />
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td className="text-start">{row.name}</td>
                  <td className="text-start">{row.server ?? ''}</td>
                  <td className="text-start">{row.accountLogin ?? ''}</td>
                  <td className="text-center align-middle">
                    <EnabledSwitch
                      checked={row.enabled}
                      disabled={setEnabled.isPending}
                      onChange={(next) => setEnabled.mutate({ id: row.id, enabled: next })}
                    />
                  </td>
                  <td>
                    <RowActionButtons
                      onEdit={() => setModal({ mode: 'edit', row })}
                      onDelete={() => setDeleteTarget(row)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <DataSourceModal
        tenantId={tenantId}
        connectionId={connection.id}
        state={modal}
        onClose={() => setModal(null)}
      />

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Delete Data Source"
        message={`Delete "${deleteTarget?.name ?? ''}"?`}
        confirmLabel="Delete"
        busy={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connectors tab (connections list + drill-down)
// ---------------------------------------------------------------------------

export function ConnectorsTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [drillInto, setDrillInto] = useState<ConnectionRow | null>(null);

  const listQuery = useQuery({
    queryKey: ['connections', tenantId],
    queryFn: () => connectionsApi.getList(tenantId, PAGE),
  });

  const [modal, setModal] = useState<ConnModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConnectionRow | null>(null);

  const setEnabled = useMutation({
    mutationFn: (p: { id: string; enabled: boolean }) =>
      connectionsApi.setEnabled(p.id, p.enabled, tenantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['connections', tenantId] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => connectionsApi.remove(id, tenantId),
    onSuccess: () => {
      toast.success('Connection deleted.');
      setDeleteTarget(null);
      void qc.invalidateQueries({ queryKey: ['connections', tenantId] });
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete.');
      setDeleteTarget(null);
    },
  });

  if (drillInto) {
    return (
      <DataSourcesView tenantId={tenantId} connection={drillInto} onBack={() => setDrillInto(null)} />
    );
  }

  if (listQuery.isLoading) return <Spinner />;
  if (listQuery.isError) return <ErrorState onRetry={() => listQuery.refetch()} />;

  const rows = listQuery.data?.items ?? [];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Connection Details</h5>
        <button type="button" className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>
          <i className="fa fa-plus me-1" />
          Add Connection
        </button>
      </div>

      <table className="table dataTable table-striped table-hover w-100 mb-0">
        <thead>
          <tr>
            <th className="text-start">Name</th>
            <th className="text-start">Connection Type</th>
            <th className="text-center">Enabled</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <EmptyState message="No connections." />
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id}>
                <td className="text-start">{row.name}</td>
                <td className="text-start">{row.dataSourceType}</td>
                <td className="text-center align-middle">
                  <EnabledSwitch
                    checked={row.enabled}
                    disabled={setEnabled.isPending}
                    onChange={(next) => setEnabled.mutate({ id: row.id, enabled: next })}
                  />
                </td>
                <td>
                  <RowActionButtons
                    onEdit={() => setModal({ mode: 'edit', row })}
                    onDelete={() => setDeleteTarget(row)}
                    extra={
                      isTerminalConnectorType(row.dataSourceType) ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          title="Edit Data Sources"
                          onClick={() => setDrillInto(row)}
                        >
                          <i className="fa fa-database" />
                        </button>
                      ) : null
                    }
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <ConnectionModal tenantId={tenantId} state={modal} onClose={() => setModal(null)} />

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Delete Connection"
        message={`Delete "${deleteTarget?.name ?? ''}"? This also removes its data sources.`}
        confirmLabel="Delete"
        busy={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
